import React, { useState, useEffect } from 'react';
import { LoadingInline } from './LoadingOverlay';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../services/api';
import './TicketView.css';

// ticketToken: mail linkinden geldiğinde doldurulur, login gerektirmeden public endpoint'i çağırır
const TicketView = ({ reservationId, onBack, ticketToken }) => {
  const [reservation, setReservation] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [venueName, setVenueName] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => { loadReservationAndTickets(); }, [reservationId]);

  const loadReservationAndTickets = async () => {
    try {
      let resData;
      if (ticketToken) {
        // Public erişim — mail linki, login gerektirmez
        const res = await fetch(
          `${API_URL}/api/reservations/${reservationId}/ticket?t=${encodeURIComponent(ticketToken)}`
        );
        if (!res.ok) { setLoading(false); return; }
        resData = await res.json();
      } else {
        resData = await api.getReservation(reservationId);
      }
      if (!resData || resData.status === 404) { setLoading(false); return; }

      setReservation(resData);

      if (resData.venueId) {
        try {
          const venueData = await api.getVenue(resData.venueId);
          setVenueName(venueData?.name ?? '');
        } catch { setVenueName(''); }
      }

      const syntheticTickets = (resData.seats || []).map((seat, idx) => ({
        id:            `${resData.id}_${idx}`,
        reservationId: resData.id,
        eventName:     resData.eventName,
        eventDate:     resData.eventDate,
        seatId:        seat.seatId,
        sectionName:   seat.sectionName,
        ticketType:    seat.ticketType,
        price:         seat.finalPrice ?? 0,
        qrCode:        `${resData.id}|${seat.seatId}`,
        isUsed:        false,
        customerName:  resData.customerName,
      }));

      setTickets(syntheticTickets);
      setLoading(false);
    } catch (err) {
      console.error('Veri yükleme hatası:', err);
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const token = localStorage.getItem('gg_access_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/reservations/${reservationId}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('PDF alınamadı');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `bilet-${reservationId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF hatası:', err);
      alert('PDF oluşturulamadı.');
    }
  };

  const handlePrintTicket = (ticketId) => {
    const ticketElement = document.getElementById(`ticket-${ticketId}`);
    if (ticketElement) {
      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write(`
        <html>
          <head>
            <title>Bilet - ${ticketId}</title>
            <style>body { font-family: Arial, sans-serif; padding: 20px; }</style>
          </head>
          <body>${ticketElement.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getTicketTypeName  = (type) => ({ 0: 'Tam Bilet', 1: 'Öğrenci', 2: 'Davetli' })[type] || 'Bilinmiyor';
  const getTicketTypeColor = (type) => ({ 0: '#2e00af', 1: '#2196F3', 2: '#9C27B0' })[type]   || '#6c757d';

  if (loading) return <LoadingInline message="Biletler yükleniyor..." />;

  if (!reservation) {
    return (
      <div className="error-view">
        <h2><i className="fa-solid fa-circle-xmark" /> Rezervasyon Bulunamadı</h2>
        <button onClick={onBack} className="back-btn">← Geri Dön</button>
      </div>
    );
  }

  return (
    <div className="ticket-view">
      <div className="ticket-view-header">
        <button onClick={onBack} className="back-btn">← Geri</button>
        <h2><i className="fa-solid fa-ticket" /> Biletleriniz</h2>
        {reservation?.paymentReceived && (
          <button onClick={handleDownloadPdf} className="download-pdf-btn">
            <i className="fa-solid fa-download" /> PDF İndir
          </button>
        )}
      </div>

      <div className="reservation-info">
        <h3><i className="fa-solid fa-clipboard-list" /> Rezervasyon Bilgileri</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Rezervasyon No:</span>
            <span className="info-value">{reservation.id}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Müşteri:</span>
            <span className="info-value">{reservation.customerName}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span className="info-value">{reservation.customerEmail}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Telefon:</span>
            <span className="info-value">{reservation.customerPhone}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Toplam Tutar:</span>
            <span className="info-value">₺{reservation.totalAmount.toFixed(2)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Ödeme Durumu:</span>
            <span className={`payment-status ${reservation.paymentReceived ? 'paid' : 'unpaid'}`}>
              {reservation.paymentReceived
                ? <><i className="fa-solid fa-circle-check" /> Ödendi</>
                : <><i className="fa-solid fa-clock" /> Bekliyor</>}
            </span>
          </div>
        </div>
      </div>

      {!reservation.paymentReceived ? (
        <div className="payment-pending-notice">
          <div className="payment-pending-icon"><i className="fa-solid fa-clock" /></div>
          <h3>Rezervasyonunuz Alındı</h3>
          <div className="reservation-id-display" style={{marginBottom: 16}}>
            Rezervasyon No: <strong>{reservation.id}</strong>
          </div>
          <p>
            Biletleriniz için ilgili kuruma ödeme yapmanız bekleniyor.
            Ödemeniz kurum tarafından onaylandıktan sonra biletleriniz{' '}
            <strong>{reservation.customerEmail}</strong> adresinize gönderilecek
            ve görüntülenebilir hale gelecektir.
          </p>
        </div>
      ) : (
        <div className="tickets-container">
          <h3><i className="fa-solid fa-ticket" /> Biletler ({tickets.length} Adet)</h3>

          <div className="tickets-grid">
            {tickets.map(ticket => (
              <div key={ticket.id} id={`ticket-${ticket.id}`} className="ticket-card">
                <div className="ticket-header" style={{ background: getTicketTypeColor(ticket.ticketType) }}>
                  <h4><i className="fa-solid fa-masks-theater" /> ETKİNLİK BİLETİ</h4>
                </div>

                <div className="ticket-body">
                  <div className="ticket-event-info">
                    <h3>{ticket.eventName}</h3>
                    <p className="ticket-date">
                      <i className="fa-solid fa-calendar" />{' '}
                      {new Date(ticket.eventDate).toLocaleDateString('tr-TR', {
                        day: 'numeric', month: 'long', year: 'numeric',
                        weekday: 'long', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                    <p className="ticket-venue"><i className="fa-solid fa-location-dot" /> {venueName}</p>
                  </div>

                  <div className="ticket-seat-info">
                    <div className="seat-detail">
                      <span className="label">Bölüm</span>
                      <span className="value">{ticket.sectionName}</span>
                    </div>
                    <div className="seat-detail highlight">
                      <span className="label">Koltuk</span>
                      <span className="value seat-number">{ticket.seatId}</span>
                    </div>
                  </div>

                  <div className="ticket-pricing">
                    <div className="price-detail">
                      <span className="label">Bilet Tipi</span>
                      <span className="value">{getTicketTypeName(ticket.ticketType)}</span>
                    </div>
                    <div className="price-detail">
                      <span className="label">Ücret</span>
                      <span className="value price">₺{ticket.price.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="ticket-customer">
                    <span className="label">Müşteri</span>
                    <span className="value">{ticket.customerName}</span>
                  </div>

                  <div className="ticket-qr">
                    <p className="qr-instruction">Giriş için QR kodu gösterin</p>
                    <div className="qr-code-container">
                      <QRCodeSVG value={ticket.qrCode} size={150} level="H" includeMargin={true} />
                    </div>
                  </div>

                  <div className="ticket-id">Bilet No: {ticket.id}</div>

                  <div className="ticket-status">
                    {ticket.isUsed
                      ? <span className="status-badge used"><i className="fa-solid fa-circle-check" /> Kullanıldı</span>
                      : <span className="status-badge active"><i className="fa-solid fa-circle-check" /> Aktif</span>}
                  </div>

                  <div className="ticket-footer">Bu bilet tek kullanımlıktır</div>
                </div>

                <div className="ticket-actions">
                  <button onClick={() => handlePrintTicket(ticket.id)} className="print-btn">
                    <i className="fa-solid fa-print" /> Yazdır
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Email bilgisi — ödeme alındıysa */}
          <div className="email-info">
            <p>
              <i className="fa-solid fa-envelope" /> Biletleriniz{' '}
              <strong>{reservation.customerEmail}</strong> adresinize gönderilecektir.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketView;